'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { trackEnterpriseInquirySubmit } from '@/lib/analytics';
import { useRouter } from 'next/navigation';

const budgetOptions = [
  { value: 'under_5k', label: 'Under ₹5,000' },
  { value: '5k_15k', label: '₹5,000 – ₹15,000' },
  { value: '15k_50k', label: '₹15,000 – ₹50,000' },
  { value: '50k_1l', label: '₹50,000 – ₹1,00,000' },
  { value: 'above_1l', label: 'Above ₹1,00,000' },
];

interface FormState {
  company_name: string;
  email: string;
  phone: string;
  monthly_budget: string;
  message: string;
}

const initialForm: FormState = {
  company_name: '',
  email: '',
  phone: '',
  monthly_budget: '',
  message: '',
};

export default function EnterpriseInquiryForm() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.company_name || !form.email || !form.phone || !form.monthly_budget) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      const { error: dbError } = await supabase.from('seller_inquiries').insert({
        company_name: form.company_name,
        email: form.email,
        phone: form.phone,
        monthly_budget: form.monthly_budget,
        message: form.message || null,
      });
      if (dbError) {
        setError('Failed to submit inquiry. Please try again.');
        console.error('Inquiry submission error:', dbError.message);
      } else {
        setForm(initialForm);
        trackEnterpriseInquirySubmit(form.monthly_budget);
        router.push('/for-brands/submission-success?type=inquiry');
      }
    } catch (err: any) {
      setError('Something went wrong. Please try again.');
      console.error('Inquiry error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enterprise-inquiry" className="section-py bg-surface-variant">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Info */}
          <div className="space-y-6">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary bg-primary-container px-3 py-1 rounded-full mb-3">
                Enterprise
              </span>
              <h2 className="font-heading font-bold text-foreground mb-3" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                Let's Get Your Products Reviewed
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Looking for 50+ reviewers, custom targeting, or a dedicated review manager? Tell us about your brand and products — we'll get back within 24 hours.
              </p>
            </div>
            <div className="space-y-4">
              {[
                { icon: 'UserGroupIcon', title: '50+ Verified Creators', desc: 'Custom demographic targeting by city, age, and product category.' },
                { icon: 'SparklesIcon', title: 'AI Quality Check', desc: 'Every review is AI-verified for authenticity and quality.' },
                { icon: 'DocumentTextIcon', title: 'GST Invoice Included', desc: 'Compliant invoicing for all enterprise review programs.' },
                { icon: 'ChartBarIcon', title: 'Real-time Dashboard', desc: 'Track reviews, ratings, and feedback progress live.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#FFE0D0' }}>
                    <Icon name={item.icon as 'UserGroupIcon'} size={16} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-foreground text-sm">{item.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-card rounded-3xl border border-border p-8">
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 shrink-0" />
                <span className="text-xs text-red-600">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <h3 className="font-heading font-bold text-foreground text-lg mb-1">Product Review Inquiry</h3>
              <p className="text-xs text-muted-foreground mb-4">All fields marked * are required.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Company Name *</label>
                  <input
                    type="text"
                    name="company_name"
                    value={form.company_name}
                    onChange={handleChange}
                    placeholder="Your brand or company"
                    className="w-full border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-background transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Work Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className="w-full border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-background transition-all"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="w-full border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-background transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Monthly Budget *</label>
                  <select
                    name="monthly_budget"
                    value={form.monthly_budget}
                    onChange={handleChange}
                    className="w-full border border-border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-background transition-all"
                    required
                  >
                    <option value="">Select budget range</option>
                    {budgetOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Message (Optional)</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us about your products, review goals, or any specific requirements..."
                  className="w-full border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-background transition-all resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full cta-gradient text-white font-heading font-bold py-3.5 rounded-xl text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={16} />
                    Submit Inquiry
                  </>
                )}
              </button>
              <p className="text-center text-xs text-muted-foreground">
                We'll respond within 24 hours. No spam, ever.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
